import { debounce } from '../utils/debounce.ts';
import { isPageHidden } from '../utils/pagevisibility-api.ts';
import { throttle } from '../utils/throttle.ts';

const SCROLL_LISTENER_INTERVAL: number = 50;
const RESIZE_LISTENER_INTERVAL: number = 200;

/**
 * Services to provide throttled or debounced window events to components.
 *
 * To improve performance, we do not listen to window's scroll events directly. Instead, this service samples the
 * current scroll position every $SCROLL_LISTENER_INTERVAL milliseconds and reports any changes to all registered
 * observers.
 *
 * Resize events are debounced such that changing the browser size does not trigger unnecessarily amount of events.
 */
export interface IWindowEventService {
  register(eventType: EventType, observer: IWindowEventObserver | EventListener): void;
  unregister(eventType: EventType, observer: IWindowEventObserver | EventListener): void;

  forceUpdate(eventType: EventType): void;
}

export class WindowEventService implements IWindowEventService {
  private observers: Map<EventType, (IWindowEventObserver | EventListener)[]> = new Map([
    [EventType.Scroll, []],
    [EventType.Resize, []],
    [EventType.VisibilityChange, []]
  ]);

  private pageHiddenSince: number;

  constructor() {
    window.addEventListener(
      EventType.Scroll,
      throttle(this.emitEvent(EventType.Scroll), SCROLL_LISTENER_INTERVAL, false)
    );
    window.addEventListener(
      EventType.Resize,
      debounce(this.emitEvent(EventType.Resize), RESIZE_LISTENER_INTERVAL)
    );
    document.addEventListener(EventType.VisibilityChange, () => this.emitVisibilityChangeEvent());

    // We also emit the scroll position once after a short grace period such that 'view' events (e.g. gf-track and
    // animation-trigger) inside the initial viewport are triggered as well.
    window.setTimeout(this.emitEvent(EventType.Scroll), SCROLL_LISTENER_INTERVAL);
  }

  public register(eventType: EventType, observer: IWindowEventObserver | EventListener): void {
    const currentObservers = this.observers.get(eventType);
    if (currentObservers!.indexOf(observer) === -1) {
      currentObservers!.push(observer);
    }
  }

  public unregister(eventType: EventType, observer: IWindowEventObserver | EventListener): void {
    const currentObservers = this.observers.get(eventType);
    if (currentObservers && currentObservers!.indexOf(observer) >= 0) {
      currentObservers!.splice(currentObservers.indexOf(observer), 1);
    }
  }

  public forceUpdate(eventType: EventType): void {
    this.emitEvent(eventType)(new Event(eventType));
  }

  private emitEvent(eventType: EventType): EventListener {
    return (event: Event) => {
      const currentObservers = this.observers.get(eventType) || [];
      currentObservers.forEach(observer => {
        if (typeof observer === 'function') {
          observer(event);
        } else {
          observer.listener?.(event);
        }
      });
    };
  }

  private emitVisibilityChangeEvent(): void {
    const currentObservers = this.observers.get(EventType.VisibilityChange) || [];

    if (isPageHidden()) {
      this.pageHiddenSince = Date.now();

      currentObservers.filter(isObserver).forEach(observer => observer.onPageHidden?.());
    } else {
      const hiddenDuration = Date.now() - this.pageHiddenSince;

      currentObservers
        .filter(isObserver)
        .forEach(observer => observer.onPageVisible?.(hiddenDuration));
    }
  }
}

export enum EventType {
  Scroll = 'scroll',
  Resize = 'resize',
  VisibilityChange = 'visibilitychange'
}

/*
 * Observer Interface for registering a component's event listeners
 */

export type IWindowEventObserver = {
  listener?: EventListener;

  /**
   * Callback when this page is visible.
   *
   * @param hiddenDuration  duration (in ms) of this page being hidden
   */
  onPageVisible?(hiddenDuration: number): void;

  /**
   * Callback when this page is hidden.
   */
  onPageHidden?(): void;
};

/**
 * Whether the argument is a observer object or function listener.
 */
const isObserver = (
  observer: IWindowEventObserver | EventListener
): observer is IWindowEventObserver => !(typeof observer === 'function');
